import { useRef } from "react";
import type { TranslateFn } from "../../i18n";
import { MobileBottomTabs } from "../components/MobileBottomTabs";

/**
 * Mobile-only AI scan / photo upload step.
 *
 * Layout matching the reference:
 *   ┌─ "Review your photo" title + subtitle
 *   │  ┌─ photo preview (light gray bg) ─┐
 *   │  └─────────────────────────────────┘
 *   │  ┌─ "Checklist for accuracy" card ─┐
 *   │  │ • Form-fitting clothing         │
 *   │  │ • 2-3 meters from camera        │
 *   │  │ • Neutral background            │
 *   │  └─────────────────────────────────┘
 *   │  (spacer)
 *   ├─ ANALYZE MY SIZE (dark CTA, full width)
 *   │  RETAKE PHOTO (outlined, only when photo present)
 *   └─ MANUAL FIT | AI SCAN
 */

function UploadIconLg() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="32" height="32">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="14" height="14" aria-hidden="true">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function XMarkIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="14" height="14" aria-hidden="true">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function CloseIconSm() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

interface PhotoStepMobileProps {
  photoPreview: string | null;
  handlePhotoSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleRemovePhoto: () => void;
  onAnalyze: () => void;
  onSwitchToManual: () => void;
  error: string;
  photoVariant?: "full-body" | "close-up";
  photoStepHeight?: string;
  onPhotoStepHeightChange?: (v: string) => void;
  // Controlled age-gate — parent owns the source of truth so the submit
  // handler (which lives in the parent) sees the same value the user sees.
  // null = not answered, true = 18+, false = under 18 (blocked).
  ageConfirmed: boolean | null;
  onAgeConfirmedChange: (v: boolean | null) => void;
  // Optional override for the primary CTA label (default "ANALYZE MY SIZE").
  // BodyProfileView mobile passes "Continue" because the photo step is
  // step 1 of a 2-step scan flow there.
  primaryLabel?: string;
  // When true, render the desktop-style compact inline age-verification
  // chip below the upload zone instead of the large overlay card. Used by
  // BodyProfileView mobile so the upload area isn't cropped by the gate.
  compactAgeGate?: boolean;
  // Backend age-check / image-processing state — set by parent when the
  // photo upload flow is in flight. PhotoStepMobile shows a spinner overlay
  // while processing and a red "rejected" chip when the backend says the
  // photo is under 18 or otherwise unusable.
  photoProcessing?: boolean;
  photoStatus?: string;
  photoRejection?: string | null;
  onClearRejection?: () => void;
  photoHelpSlot?: React.ReactNode;
  bodyContextSlot?: React.ReactNode;
  hideTabs?: boolean;
  t: TranslateFn;
}

export function PhotoStepMobile({
  photoPreview,
  handlePhotoSelect,
  handleRemovePhoto,
  onAnalyze,
  onSwitchToManual,
  error,
  photoVariant = "full-body",
  photoStepHeight,
  onPhotoStepHeightChange,
  ageConfirmed,
  onAgeConfirmedChange,
  primaryLabel,
  compactAgeGate = false,
  photoProcessing = false,
  photoStatus,
  photoRejection,
  onClearRejection,
  photoHelpSlot,
  bodyContextSlot,
  hideTabs = false,
  t,
}: PhotoStepMobileProps) {
  const isCloseUp = photoVariant === "close-up";
  const fileRef = useRef<HTMLInputElement>(null);
  const hasPhoto = !!photoPreview;
  const gated = !hasPhoto && ageConfirmed !== true;
  const uploadTitle = isCloseUp ? t("Upload front face") : t("Upload your photo");
  const uploadHint = isCloseUp ? t("Click or drag a front-facing photo") : t("Click or drag a full-body photo");
  const uploadFormatHint = t("JPEG, PNG, WebP, AVIF (max 10MB)");

  const emptyUploadContent = (
    <>
      <UploadIconLg />
      <span className="ps-pm-preview-empty-title">{uploadTitle}</span>
      <span className="ps-pm-preview-empty-hint">{uploadHint}</span>
      <span className="ps-pm-preview-empty-format">{uploadFormatHint}</span>
    </>
  );

  return (
    <div className="ps-pm-root">
      {/* ── Title + subtitle (always the same regardless of upload state) ── */}
      <div className="ps-pm-header">
        <h2 className="ps-pm-title">{isCloseUp ? t("Upload front face") : t("Review your photo")}</h2>
        <p className="ps-pm-subtitle">
          {isCloseUp
            ? t("Use a front-facing photo with your face and head clearly visible.")
            : t("Ensure your full body is visible for the most accurate digital tailoring scan.")}
        </p>
      </div>

      {/* ── Hidden file input ── */}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        onChange={handlePhotoSelect}
        style={{ display: "none" }}
      />

      {/* ── Photo preview / upload zone ── */}
      <div className={`ps-pm-preview${compactAgeGate ? " ps-pm-preview-tall ps-pm-preview-compact-gate" : ""}`}>
        {!hasPhoto && photoHelpSlot}
        {hasPhoto ? (
          <>
            <img src={photoPreview!} alt={t("Your photo")} className="ps-pm-preview-img" />
            <button
              type="button"
              className="ps-pm-preview-remove"
              onClick={handleRemovePhoto}
              aria-label={t("Remove photo")}
            >
              <CloseIconSm />
            </button>
          </>
        ) : (
          <>
            {compactAgeGate && gated ? (
              <div className="ps-pm-preview-empty ps-pm-preview-empty-static">
                {emptyUploadContent}
              </div>
            ) : (
              <button
                type="button"
                className={`ps-pm-preview-empty${gated && !compactAgeGate ? " ps-pm-preview-blurred" : ""}`}
                onClick={() => { if (!gated) fileRef.current?.click(); }}
                disabled={gated}
                aria-hidden={gated}
                tabIndex={gated ? -1 : 0}
              >
                {emptyUploadContent}
              </button>
            )}
            {/* Big-overlay age gate (legacy) — when caller opts out of
                compactAgeGate (e.g. older callers). Heavily blurred. */}
            {!compactAgeGate && ageConfirmed === null && (
              <div className="ps-pm-age-gate" role="dialog">
                <div className="ps-pm-age-gate-card">
                  <div className="ps-pm-age-gate-eyebrow">{t("AGE VERIFICATION")}</div>
                  <div className="ps-pm-age-gate-question">
                    {t("Is the person in this photo 18 years or older?")}
                  </div>
                  <div className="ps-pm-age-gate-actions">
                    <button type="button" className="ps-pm-age-gate-btn ps-pm-age-gate-btn-primary" onClick={() => onAgeConfirmedChange(true)}>
                      {t("Yes")}
                    </button>
                    <button type="button" className="ps-pm-age-gate-btn ps-pm-age-gate-btn-secondary" onClick={() => onAgeConfirmedChange(false)}>
                      {t("No")}
                    </button>
                  </div>
                </div>
              </div>
            )}
            {!compactAgeGate && ageConfirmed === false && (
              <div className="ps-pm-age-gate" role="alert">
                <div className="ps-pm-age-gate-card">
                  <div className="ps-pm-age-gate-eyebrow ps-pm-age-gate-eyebrow-blocked">
                    {t("UPLOAD NOT ALLOWED")}
                  </div>
                  <div className="ps-pm-age-gate-question">
                    {t("For your safety, we cannot process photos of people under 18.")}
                  </div>
                  <button type="button" className="ps-pm-age-gate-btn ps-pm-age-gate-btn-secondary" onClick={() => onAgeConfirmedChange(null)}>
                    {t("Go back")}
                  </button>
                </div>
              </div>
            )}

            {/* Compact age-gate overlay — centered chip, subtle dim,
                tiny blur. Caller opts in via compactAgeGate so the
                upload area stays large and the question stays visible
                in the centre. */}
            {compactAgeGate && ageConfirmed === null && (
              <div className="ps-pm-age-inline" role="dialog" aria-label={t("Age verification")}>
                <span className="ps-pm-age-inline-q">{t("Are you 18 or older?")}</span>
                <div className="ps-pm-age-inline-actions">
                  <button
                    type="button"
                    className="ps-pm-age-inline-btn ps-pm-age-inline-yes"
                    onClick={(e) => { e.stopPropagation(); onAgeConfirmedChange(true); }}
                  >{t("Yes")}</button>
                  <button
                    type="button"
                    className="ps-pm-age-inline-btn ps-pm-age-inline-no"
                    onClick={(e) => { e.stopPropagation(); onAgeConfirmedChange(false); }}
                  >{t("No")}</button>
                </div>
              </div>
            )}
            {compactAgeGate && ageConfirmed === false && (
              <div className="ps-pm-age-inline ps-pm-age-inline-error" role="alert">
                <span className="ps-pm-age-inline-q">{t("Under 18 — can't process this photo.")}</span>
                <div className="ps-pm-age-inline-actions">
                  <button
                    type="button"
                    className="ps-pm-age-inline-btn ps-pm-age-inline-no"
                    onClick={(e) => { e.stopPropagation(); onAgeConfirmedChange(null); }}
                  >{t("Go back")}</button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Backend age-check / processing spinner — fires after the user
            uploaded a photo while the backend (Gemini) verifies it. */}
        {photoProcessing && (
          <div className="ps-pm-processing-overlay" role="status" aria-live="polite">
            <div className="ps-pm-processing-card">
              <div className="ps-pm-processing-spinner" aria-hidden="true" />
              <span className="ps-pm-processing-label">{photoStatus || t("Analyzing photo…")}</span>
            </div>
          </div>
        )}
      </div>

      {/* Backend rejection chip — matches desktop's "Photo declined" layout.
          Sits BELOW the upload zone so the user can retry without it
          covering the upload area. */}
      {photoRejection && (
        <div className="ps-pm-photo-rejection" role="alert">
          <div className="ps-pm-photo-rejection-head">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <span className="ps-pm-photo-rejection-title">{t("Different photo needed")}</span>
          </div>
          <p className="ps-pm-photo-rejection-msg">{photoRejection}</p>
          {onClearRejection && (
            <button
              type="button"
              className="ps-pm-photo-rejection-retry"
              onClick={onClearRejection}
            >{t("Try another photo")}</button>
          )}
        </div>
      )}

      {bodyContextSlot}

      {/* ── Legal notice ── */}
      <div className="ps-pm-legal-notice">
        <div className="ps-pm-legal-notice-head">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
          <span>{t("LEGAL NOTICE")}</span>
        </div>
        <p className="ps-pm-legal-notice-body">
          {t("Your image will be used to generate a virtual try-on preview showing how selected items may look and fit. Images are processed securely and are not stored after generation.")}
        </p>
      </div>

      {/* ── Guidelines ──
          Close-up (face/selfie) keeps a compact single-card checklist.
          Full-body uses a Do / Don't grid mirroring the desktop design
          in BodyProfileView.tsx — the backend's MediaPipe anchor block
          needs head and feet in frame to calibrate a real-world ruler
          from the nose→ankle span, so the Do list emphasises that. */}
      {isCloseUp ? (
        <div className="ps-pm-checklist">
          <div className="ps-pm-checklist-icon">
            <InfoIcon />
          </div>
          <div className="ps-pm-checklist-body">
            <div className="ps-pm-checklist-title">{t("Checklist for accuracy")}</div>
            <ul className="ps-pm-checklist-items">
              <li>{t("Face the camera directly at eye level")}</li>
              <li>{t("Keep hair away from face and ears")}</li>
              <li>{t("Good lighting, plain background")}</li>
            </ul>
          </div>
        </div>
      ) : (
        <>
          <div className="ps-pm-dodonts-grid">
            <div className="ps-pm-do-card">
              <div className="ps-pm-do-card-head">
                <CheckIcon />
                <span>{t("Do")}</span>
              </div>
              <ul className="ps-pm-do-card-list">
                <li>{t("Stand facing the camera with your full body in frame")}</li>
                <li>{t("Use natural or even lighting (e.g. near a window)")}</li>
                <li>{t("Wear fitted, simple clothing (e.g. neutral colors)")}</li>
                <li>{t("Choose a plain background (a light wall is ideal)")}</li>
                <li>{t("Stand straight and still, arms relaxed by your sides")}</li>
              </ul>
            </div>
            <div className="ps-pm-dont-card">
              <div className="ps-pm-dont-card-head">
                <XMarkIcon />
                <span>{t("Don't")}</span>
              </div>
              <ul className="ps-pm-dont-card-list">
                <li>{t("Don't wear loose, baggy, or layered clothing")}</li>
                <li>{t("Don't sit, pose, or bend your body")}</li>
                <li>{t("Don't use strong backlighting")}</li>
                <li>{t("Don't take mirror photos or selfies")}</li>
                <li>{t("Don't apply filters, effects, or edits")}</li>
              </ul>
            </div>
          </div>
          <div className="ps-pm-dodonts-tip">
            <span className="ps-pm-dodonts-tip-emoji" aria-hidden="true">💡</span>
            <span>{t("The simpler your photo is, the more accurate your virtual try-on results will be.")}</span>
          </div>
        </>
      )}


      {error && <p className="ps-bpm-error">{error}</p>}

      {/* ── Spacer pushes the bottom action area down ── */}
      <div className="ps-bpm-spacer" />

      {/* ── Bottom action: primary CTA + retake + tabs ── */}
      <div className="ps-bpm-bottom">
        <button
          type="button"
          className="ps-pm-primary-btn"
          onClick={onAnalyze}
          disabled={!hasPhoto || ageConfirmed !== true}
        >
          {primaryLabel ?? t("ANALYZE MY SIZE")}
        </button>
        {hasPhoto && (
          <button
            type="button"
            className="ps-pm-secondary-btn"
            onClick={() => fileRef.current?.click()}
          >
            {t("RETAKE PHOTO")}
          </button>
        )}
        {!hideTabs && (
          <MobileBottomTabs
            mode="scan"
            onSwitchToManual={onSwitchToManual}
            onSwitchToScan={() => { /* already on scan */ }}
            t={t}
          />
        )}
      </div>
    </div>
  );
}
